/**
 * Step modes on the draft (Feature 71 D39): each step's root, the nodes a
 * write-back may target, and the edits a mode needs, built like the agent's
 * own `modes.py` so the canvas offers only what a save accepts.
 *
 * A root is the node a step's results are held by: `first`, or the
 * independent step heading its subtree. An independent step belongs to its
 * parent's subtree and is the root of the steps after it.
 */
import type {
  LinkageMode,
  LinkageStep,
  LinkageWrite,
  LinkageWriteBack,
} from '@optum/api-client/types';

import { FIRST, removeStep, type Draft } from './draft.js';

export const MERGED: LinkageMode = 'merged';
export const WRITE_BACK: LinkageMode = 'write_back';
export const INDEPENDENT: LinkageMode = 'independent';

/** A step's mode, `merged` when it has none. */
export function modeOf(step: LinkageStep): LinkageMode {
  return step.mode ?? MERGED;
}

/** The steps in an order where each follows the steps linking into it. */
function inOrder(draft: Draft): LinkageStep[] {
  const placed = new Set([FIRST]);
  const order: LinkageStep[] = [];
  let pending = [...draft.steps];
  while (pending.length > 0) {
    const ready = pending.filter((s) =>
      draft.links.filter((l) => l.to === s.name).every((l) => placed.has(l.from)),
    );
    // A loop or a dangling link leaves the rest unplaced; the save reports it.
    if (ready.length === 0) break;
    for (const step of ready) {
      placed.add(step.name);
      order.push(step);
    }
    pending = pending.filter((s) => !placed.has(s.name));
  }
  return order;
}

/** Each step's root; a step fed from two subtrees takes its first parent's. */
export function rootsOf(draft: Draft): Map<string, string> {
  const byName = new Map(draft.steps.map((s) => [s.name, s]));
  const roots = new Map<string, string>();
  for (const step of inOrder(draft)) {
    const parent = draft.links.find((l) => l.to === step.name)?.from ?? FIRST;
    const parentStep = byName.get(parent);
    roots.set(
      step.name,
      parent === FIRST
        ? FIRST
        : parentStep && modeOf(parentStep) === INDEPENDENT
          ? parent
          : (roots.get(parent) ?? FIRST),
    );
  }
  return roots;
}

/** What a write-back step may write into: its root, then the steps linking straight in (D46). */
export function allowedTargets(draft: Draft, name: string): string[] {
  const root = rootsOf(draft).get(name) ?? FIRST;
  const direct = draft.links.filter((l) => l.to === name).map((l) => l.from);
  return [...new Set([root, ...direct])];
}

function withStep(draft: Draft, name: string, change: (step: LinkageStep) => LinkageStep): Draft {
  return { ...draft, steps: draft.steps.map((s) => (s.name === name ? change(s) : s)) };
}

function withWriteBack(
  draft: Draft,
  name: string,
  change: (writeBack: LinkageWriteBack) => LinkageWriteBack,
): Draft {
  return withStep(draft, name, (s) =>
    s.write_back ? { ...s, write_back: change(s.write_back) } : s,
  );
}

/** Another mode: a write-back starts on its root with no writes; leaving it drops them. */
export function setMode(draft: Draft, name: string, mode: LinkageMode): Draft {
  const root = rootsOf(draft).get(name) ?? FIRST;
  return withStep(draft, name, (s) => {
    if (modeOf(s) === mode) return s;
    const { write_back: _dropped, ...rest } = s;
    return mode === WRITE_BACK
      ? { ...rest, mode, write_back: { target: root, fields: [] } }
      : { ...rest, mode };
  });
}

export function setTarget(draft: Draft, name: string, target: string): Draft {
  return withWriteBack(draft, name, (wb) => ({ ...wb, target }));
}

export function addWrite(draft: Draft, name: string, write: LinkageWrite): Draft {
  return withWriteBack(draft, name, (wb) => ({ ...wb, fields: [...wb.fields, write] }));
}

export function setWrite(draft: Draft, name: string, index: number, write: LinkageWrite): Draft {
  return withWriteBack(draft, name, (wb) => ({
    ...wb,
    fields: wb.fields.map((w, i) => (i === index ? write : w)),
  }));
}

export function removeWrite(draft: Draft, name: string, index: number): Draft {
  return withWriteBack(draft, name, (wb) => ({
    ...wb,
    fields: wb.fields.filter((_, i) => i !== index),
  }));
}

/** A step removed; a write-back that targeted it writes into its root instead. */
export function removeStepAndRetarget(draft: Draft, name: string): Draft {
  const removed = removeStep(draft, name);
  const roots = rootsOf(removed);
  return {
    ...removed,
    steps: removed.steps.map((s) =>
      s.write_back && s.write_back.target === name
        ? { ...s, write_back: { ...s.write_back, target: roots.get(s.name) ?? FIRST } }
        : s,
    ),
  };
}
